
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Award, Users, BookOpen, Activity, Briefcase, Lightbulb, Handshake } from "lucide-react";
import PartnersCarousel from "@/components/products/PartnersCarousel";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";

const MeistaPage = () => {
  const isMobile = useIsMobile();
  const seoData = getEnhancedSeoDataForRoute("/meista");
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 p-4 md:p-8">
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/meista"
        schema={seoData.schema}
      />
      
      <div className="container mx-auto max-w-5xl px-4 lg:px-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-blue-700 text-center">
          Meistä
        </h1>
        
        {isMobile && (
          <div className="mb-6">
            <Link to="/kilpailuta">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-lg shadow-md text-base flex items-center justify-center">
                <span className="text-lg">Kilpailuta lainasi</span>
              </Button>
            </Link>
          </div>
        )}
        
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 md:p-8 rounded-xl mb-8 md:mb-12 shadow-lg text-white">
          <div className="flex flex-col md:flex-row items-center mb-4">
            <Award className="h-8 w-8 md:h-10 md:w-10 mb-2 md:mb-0 md:mr-4 flex-shrink-0" />
            <h2 className="text-xl md:text-2xl font-bold">Lainasampo.fi - Suomen johtava lainavertailupalvelu</h2>
          </div>
          <p className="mb-4 text-white/90 text-base md:text-lg">
            Lainasampo.fi auttaa suomalaisia löytämään edullisimman lainan 
            helposti ja nopeasti. Olemme erikoistuneet erilaisten lainojen vertailuun ja kilpailuttamiseen.
          </p>
          <p className="text-white/90 text-base md:text-lg">
            Palvelumme kautta voit hakea lainatarjouksia useilta pankeilta ja rahoituslaitoksilta yhdellä 
            hakemuksella. Säästät aikaa ja rahaa, kun sinun ei tarvitse asioida jokaisen pankin kanssa erikseen.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
          <Card className="bg-white hover:shadow-lg transition-shadow overflow-hidden border-none shadow-md">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 flex items-center">
                <Briefcase className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg md:text-xl font-semibold text-blue-700">Toiminta-ajatuksemme</h3>
              </div>
              <div className="p-5">
                <p className="text-gray-700 mb-4 text-base">
                  Uskomme, että jokaisella pitäisi olla mahdollisuus löytää itselleen paras lainatarjous helposti ja 
                  läpinäkyvästi. Lainasampo.fi tekee lainojen vertailusta yksinkertaista ja nopeaa.
                </p>
                <p className="text-gray-700 text-base">
                  Teemme tiivistä yhteistyötä luotettavien pankkien ja rahoituslaitosten kanssa varmistaaksemme, 
                  että asiakkaamme saavat aina kilpailukykyisiä tarjouksia.
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white hover:shadow-lg transition-shadow overflow-hidden border-none shadow-md">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 flex items-center">
                <Lightbulb className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg md:text-xl font-semibold text-blue-700">Arvomme</h3>
              </div>
              <div className="p-5">
                <ul className="space-y-3 text-gray-700 text-base">
                  <li className="flex items-start">
                    <span className="inline-block bg-blue-100 text-blue-700 rounded-full p-1 mr-2 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span><span className="font-medium">Läpinäkyvyys</span> - Tarjoamme selkeää tietoa lainoista ja niiden ehdoista</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block bg-blue-100 text-blue-700 rounded-full p-1 mr-2 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span><span className="font-medium">Asiakaslähtöisyys</span> - Asiakkaidemme tarpeet ja toiveet ohjaavat toimintaamme</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block bg-blue-100 text-blue-700 rounded-full p-1 mr-2 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span><span className="font-medium">Luotettavuus</span> - Toimimme aina vastuullisesti ja luotettavasti</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block bg-blue-100 text-blue-700 rounded-full p-1 mr-2 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span><span className="font-medium">Jatkuva kehitys</span> - Kehitämme palveluamme jatkuvasti paremmaksi</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
          <Card className="bg-white hover:shadow-lg transition-shadow overflow-hidden border-none shadow-md lg:col-span-1">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 flex items-center">
                <Activity className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg font-semibold text-blue-700">Digitaalinen lainapalvelu</h3>
              </div>
              <div className="p-5">
                <p className="text-gray-700 text-base">
                  Lainasampo.fi on täysin digitaalinen palvelu, joka toimii ympäri vuorokauden. 
                  Voit hakea lainatarjouksia milloin tahansa, missä tahansa. Palvelumme on optimoitu 
                  toimimaan sujuvasti kaikilla laitteilla.
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white hover:shadow-lg transition-shadow overflow-hidden border-none shadow-md lg:col-span-2">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 flex items-center">
                <Users className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg md:text-xl font-semibold text-blue-700">Tiimimme</h3>
              </div>
              <div className="p-5">
                <p className="text-gray-700 mb-4 text-base">
                  Lainasampo.fi:n takana on joukko rahoitusalan ja digitaalisten palvelujen ammattilaisia. 
                  Tiimimme koostuu asiantuntijoista, joilla on vankka kokemus pankkialalta, fintech-sektorilta 
                  ja asiakaspalvelusta.
                </p>
                <p className="text-gray-700 text-base">
                  Koko tiimimme on sitoutunut auttamaan sinua löytämään parhaan mahdollisen rahoitusratkaisun 
                  tarpeisiisi. Olemme ylpeitä voidessamme tarjota henkilökohtaista palvelua suomeksi jokaiselle 
                  asiakkaallemme.
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="font-bold text-blue-700 text-lg">15+</p>
                    <p className="text-gray-600 text-sm">Kumppanipankkia</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="font-bold text-blue-700 text-lg">8v+</p>
                    <p className="text-gray-600 text-sm">Kokemusta</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="font-bold text-blue-700 text-lg">24/7</p>
                    <p className="text-gray-600 text-sm">Palvelu</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="font-bold text-blue-700 text-lg">100k+</p>
                    <p className="text-gray-600 text-sm">Asiakasta</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="mb-8 md:mb-12">
          <PartnersCarousel altTitle="Kumppanipankkimme" />
        </div>
        
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 md:p-8 rounded-xl text-white shadow-lg">
          <div className="flex flex-col md:flex-row items-center mb-6">
            <BookOpen className="h-8 w-8 md:h-10 md:w-10 mb-2 md:mb-0 md:mr-4 flex-shrink-0" />
            <h2 className="text-xl md:text-2xl font-bold text-center md:text-left">Haluatko tietää lisää?</h2>
          </div>
          <p className="mb-6 text-white/90 text-base md:text-lg text-center md:text-left">
            Jos sinulla on kysyttävää tai haluat lisätietoja palveluistamme, ota yhteyttä 
            asiakaspalveluumme. Vastaamme mielellämme kaikkiin kysymyksiisi!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
            <Link 
              to="/yhteydenotto" 
              className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors text-center shadow-md"
            >
              Ota yhteyttä
            </Link>
            <Link 
              to="/kilpailuta" 
              className="inline-block bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-400 transition-colors text-center shadow-md"
            >
              Kilpailuta lainasi
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeistaPage;
